import { useState, useEffect } from "react";
import { X, Download, Smartphone } from "lucide-react";
import { Button } from "./ui/button";

export default function InstallAppBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if user dismissed before (within 7 days)
    const dismissed = localStorage.getItem('installBannerDismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      const daysDiff = (now - dismissedDate) / (1000 * 60 * 60 * 24);
      if (daysDiff < 7) {
        return; // Don't show if dismissed within 7 days
      }
    }

    // Listen for beforeinstallprompt event (Android/Desktop Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show banner after a delay if not installed
    if (iOS && !standalone) {
      setTimeout(() => setShowBanner(true), 3000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('installBannerDismissed', new Date().toISOString());
  };

  // Don't show if already installed
  if (isStandalone || !showBanner) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-6 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 z-40 animate-in slide-in-from-bottom-5"
      data-testid="install-app-banner"
    >
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
        aria-label="Cerrar"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex items-start gap-4">
        <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <Smartphone className="h-7 w-7 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Instala BulkBytes
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Añade la app a tu pantalla de inicio para acceder más rápido
          </p>

          {isIOS ? (
            <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
              <p className="font-medium mb-1">En Safari:</p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Toca el icono de compartir <span className="inline-block">⬆️</span></li>
                <li>Selecciona "Añadir a pantalla de inicio"</li>
              </ol>
            </div>
          ) : (
            <Button 
              onClick={handleInstall}
              className="w-full bg-orange-500 hover:bg-orange-600"
              data-testid="install-app-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Instalar App
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
